import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Delta Terroir",
    body: "Where the rivers meet, the soil runs deep and mineral. Cool evening air rolls off the water each night, slowing ripening and locking in acidity — the quiet engine behind every wine we make.",
    img: "https://images.unsplash.com/photo-1543418219-44e30b057fea?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Vineyard at sunset in the California Delta",
  },
  {
    n: "02",
    title: "Small-Lot Fermentation",
    body: "Each block is picked by hand and fermented apart, in lots small enough to know by name. Nothing rushed, nothing forced — native character over technique, every vintage.",
    img: "https://images.unsplash.com/photo-1764649841527-c8852b63cc53?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Wine resting in the cellar",
  },
  {
    n: "03",
    title: "The Winemaker",
    body: "Javier Quintero leads the cellar with a simple rule: intervene less, taste more. His blends are built barrel by barrel, and nothing is bottled until it feels honest.",
    img: "https://images.unsplash.com/photo-1553361371-9b22f78e8b1d?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Red wine being poured",
  },
  {
    n: "04",
    title: "Honest Bottling",
    body: "No wine leaves the cellar on a schedule. It leaves when the blend is ready — structured reds built to age, whites and rosés captured at their brightest.",
    img: "https://images.unsplash.com/photo-1765850257843-aa029ab7769c?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Bottles resting in the Delta Breeze cellar",
  },
];

const Chapter = ({ chapter, flip }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div ref={ref} className={`grid md:grid-cols-2 gap-10 lg:gap-20 items-center ${flip ? "md:[&>*:first-child]:order-2" : ""}`}>
      <Reveal>
        <div className="relative overflow-hidden aspect-[4/5] spotlight-card">
          <motion.img
            src={chapter.img}
            alt={chapter.alt}
            style={{ y: imgY }}
            className="absolute inset-0 w-full h-[116%] object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/50 to-transparent" />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <span className="font-serif-display italic font-light text-7xl lg:text-8xl text-[#D4AF37]/25 leading-none block">
            {chapter.n}
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="font-serif-display text-3xl lg:text-4xl font-light text-[#F4F1EA] mt-4">{chapter.title}</h3>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="gold-hairline w-24 my-6" />
        </Reveal>
        <Reveal delay={0.25}>
          <p className="text-base text-[#A39E93] leading-relaxed max-w-md">{chapter.body}</p>
        </Reveal>
      </div>
    </div>
  );
};

export const Story = () => (
  <section id="story" data-testid="story-section" className="relative py-28 lg:py-40 bg-[#0A0908]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHeading overline="The Manifesto — In Four Chapters" title="A wine is a place, remembered." className="mb-20 lg:mb-28 max-w-2xl" />
      <div className="flex flex-col gap-24 lg:gap-36">
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.n} chapter={c} flip={i % 2 === 1} />
        ))}
      </div>
    </div>
  </section>
);
